import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Box, CircularProgress, Stack, Switch } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { ActionMenu } from 'components/_atoms';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import { ServerErrorResponse } from 'data_sources/constants';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import errorHelper from 'helpers/errorHelper';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import { DraftManualDetailContext } from './context';
import { useTrackChanges } from 'components/ContentEditor/context/SharedTrackChangesContext';
import ExportModal, { ExportType } from './ExportModal';
import useDraftPolicy from '../_data_sources/useDraftPolicy';
import useDraftPreface from '../_data_sources/useDraftPreface';
import useHasAccreditationSolution from 'views/App/Portal/AWB/_hooks/useHasAccreditationSolution';
import { AppContext } from 'views/App/context';

interface ManualActionsProps {
  manual: any;
  manualHasPermissions: (manualId: string, permissions: string[]) => boolean;
  currentUserId?: string;
  // Optional props for policy editor context
  showContentFilters?: boolean;
  enabledFilter?: boolean;
  disabledFilter?: boolean;
  onEnabledFilterChange?: (enabled: boolean) => void;
  onDisabledFilterChange?: (disabled: boolean) => void;
  // Compare policy props
  showComparePolicy?: boolean;
  isComparePolicyOpen?: boolean;
  onComparePolicyChange?: (isOpen: boolean) => void;
  canComparePolicy?: boolean;
  // Policy structure props
  currentPolicyData?: any;
  isOpenModifyPolicy?: boolean;
  setIsOpenModifyPolicy?: (isOpen: boolean) => void;
  policyMode?: string;
  showAccreditationTags?: boolean;
  setShowAccreditationTags?: (show: boolean) => void;
}

const ManualActions: React.FC<ManualActionsProps> = ({
  manual,
  manualHasPermissions,
  currentUserId,
  showContentFilters = false,
  showComparePolicy = false,
  isComparePolicyOpen = false,
  onComparePolicyChange,
  canComparePolicy = false,
  currentPolicyData,
  isOpenModifyPolicy = false,
  setIsOpenModifyPolicy,
  policyMode,
  showAccreditationTags = true,
  setShowAccreditationTags,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [gettingLock, setGettingLock] = useState<boolean>(false);
  const { getLock } = useManageManual();
  const {
    node,
    isAttachmentLibraryOpen,
    setIsAttachmentLibraryOpen,
    enabledFilter,
    setEnabledFilter,
    disabledFilter,
    setDisabledFilter,
  } = useContext(DraftManualDetailContext);
  const { showChanges, setShowChanges } = useTrackChanges();
  const { isFeatureEnabled } = useContext(AppContext);
  const hasAwbSubscription = useHasAccreditationSolution();

  const [type, id] = node ? node.split(':') : [null, null];

  const { data: policyData } = useDraftPolicy(
    manual?.id,
    type === 'policy' && id ? id : undefined,
    true,
    {},
    undefined
  );
  const { data: prefaceData } = useDraftPreface(
    manual?.id,
    type === 'preface' && id ? id : undefined,
    true,
    {},
    undefined
  );

  const isCurrentContentDisabled = () => {
    if (!node) return false;

    if (type === 'policy') {
      return policyData?.state === 'DISABLED';
    }

    if (type === 'preface') {
      return prefaceData?.state === 'DISABLED';
    }

    return false;
  };

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportType>('manual');

  const isManualLocked = manual.isLocked;
  const manualLockOwnerId = manual.lock?.userId;
  const isLockedByCurrentUser = isManualLocked && manualLockOwnerId === currentUserId;

  const handleManageStructureClick = () => {
    setGettingLock(true);
    if (!isManualLocked) {
      getLock(
        manual.id,
        async (lock) => {
          queryClient.invalidateQueries(['draft_manual', manual.id]);
          await queryClient.fetchQuery(['draft_manual', manual.id]);
          setGettingLock(false);
          navigate(`/admin/policy/draft-manuals/${manual.id}/manual-structure`);
        },
        (error: ServerErrorResponse) => {
          errorHelper.enqueueErrors(error);
          setGettingLock(false);
        }
      );
    } else if (isLockedByCurrentUser) {
      setGettingLock(false);
      navigate(`/admin/policy/draft-manuals/${manual.id}/manual-structure`);
    }
  };

  const handleEnabledFilterToggle = () => {
    const newEnabledValue = !enabledFilter;
    setEnabledFilter(newEnabledValue);

    // If turning off enabled would result in both being off, turn on disabled
    if (!newEnabledValue && !disabledFilter) {
      setDisabledFilter(true);
    }
  };

  const handleDisabledFilterToggle = () => {
    const newDisabledValue = !disabledFilter;
    setDisabledFilter(newDisabledValue);

    // If turning off disabled would result in both being off, turn on enabled
    if (!newDisabledValue && !enabledFilter) {
      setEnabledFilter(true);
    }
  };

  const setManageMenuItems = () => {
    const actions: ActionMenuItemProps[] = [];
    if (manualHasPermissions(manual.id, ['updateDraftManual'])) {
      actions.push({
        key: t('draftManuals.menu.manageStructure'),
        label: t('draftManuals.menu.editManualStructure'),
        icon: isManualLocked && !isLockedByCurrentUser && <LockIcon color='neutral' />,
        onClick: () => handleManageStructureClick(),
        disabled: (isManualLocked && !isLockedByCurrentUser) || gettingLock,
      });
    }

    // Add policy structure action if we're on a policy editor page, have current policy data, and policy is in EDITING mode (locked by current user)
    if (currentPolicyData && setIsOpenModifyPolicy && policyMode === 'EDITING') {
      actions.push({
        key: 'policyStructure',
        label: t('draftManuals.policy.editPolicyStructure'),
        onClick: () => setIsOpenModifyPolicy(!isOpenModifyPolicy),
      });
    }

    // Add generate changeset action if conditions are met
    if (
      currentPolicyData &&
      type === 'policy' &&
      id &&
      manual.manualLevel?.toLowerCase() === 'jurisdiction' &&
      manualHasPermissions(manual.id, ['issuePolicyChangeSets'])
    ) {
      actions.push({
        key: 'generateChangeset',
        label: t('draftManuals.menu.generateChangeset'),
        onClick: () => {
          navigate(`/admin/policy/draft-manuals/${manual.id}/policy:${id}/generate-changeset`);
        },
      });
    }

    actions.push({
      key: t('draftManuals.menu.attachmentLibrary'),
      label: t('draftManuals.menu.attachmentLibrary'),
      onClick: () => setIsAttachmentLibraryOpen(!isAttachmentLibraryOpen),
    });
    return actions;
  };

  const handleTrackChanges = () => {
    setShowChanges((prev) => !prev);
  };

  const handleExportClick = (type: ExportType) => {
    setExportType(type);
    setExportModalOpen(true);
  };

  const setExportMenuItems = () => {
    const actions: ActionMenuItemProps[] = [];

    actions.push({
      key: 'exportManual',
      label: t('draftManuals.export.manual'),
      onClick: () => handleExportClick('manual'),
    });

    actions.push({
      key: 'exportTableOfContents',
      label: t('draftManuals.export.tableOfContents'),
      onClick: () => handleExportClick('tableOfContents'),
    });

    if (node && (node.startsWith('policy:') || node.startsWith('preface:')) && !isCurrentContentDisabled()) {
      const [type] = node.split(':');
      const label =
        type === 'policy' ? t('draftManuals.export.currentPolicy') : t('draftManuals.export.currentPreface');

      actions.push({
        key: 'exportCurrentContent',
        label,
        onClick: () => handleExportClick('currentContent'),
      });
    }

    return actions;
  };

  const handleAccreditationTagsToggle = () => {
    if (setShowAccreditationTags) {
      setShowAccreditationTags(!showAccreditationTags);
    }
  };

  const setViewMenuItems = () => {
    const actions: ActionMenuItemProps[] = [];

    actions.push({
      key: 'enabledContent',
      label: t('draftManuals.manualStructure.filters.showEnabledContent'),
      onClick: handleEnabledFilterToggle,
      icon: (
        <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Switch size="xs" checked={enabledFilter} onChange={handleEnabledFilterToggle} />
        </Box>
      ),
    });
    actions.push({
      key: 'disabledContent',
      label: t('draftManuals.manualStructure.filters.showDisabledContent'),
      onClick: handleDisabledFilterToggle,
      icon: (
        <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Switch size="xs" checked={disabledFilter} onChange={handleDisabledFilterToggle} />
        </Box>
      ),
    });

    // Always show track changes toggle (uses sticky state)
    actions.push({
      key: 'trackChanges',
      label: t('contentEditor.toolbar.showPolicyChanges'),
      onClick: handleTrackChanges,
      icon: (
        <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Switch size="xs" checked={showChanges} onChange={handleTrackChanges} />
        </Box>
      ),
    });

    // Add accreditation tags toggle if callback is provided (uses sticky state)
    if (setShowAccreditationTags && hasAwbSubscription) {
      actions.push({
        key: 'accreditationTags',
        label: t('draftManuals.menu.showAccreditationTags'),
        onClick: handleAccreditationTagsToggle,
        icon: (
          <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
            <Switch size="xs" checked={showAccreditationTags} onChange={handleAccreditationTagsToggle} />
          </Box>
        ),
      });
    }

    if (showComparePolicy && canComparePolicy && onComparePolicyChange) {
      // Add divider if there are items above
      if (actions.length > 0) {
        actions.push({
          key: 'divider',
          label: '',
          divider: true,
        });
      }
      actions.push({
        key: 'comparePolicy',
        label: t('draftManuals.policy.comparePolicy'),
        onClick: () => onComparePolicyChange(!isComparePolicyOpen),
      });
    }
    return actions;
  };

  return (
    <>
      <Stack
        direction='row'
        spacing={4}
      >
        {gettingLock && <CircularProgress sx={{ mr: 2 }} />}
        <ActionMenu
          id='manual_actions'
          btnText={t('draftManuals.menu.manage')}
          items={setManageMenuItems()}
          btnProps={{
            size: 'small',
            variant: 'outlined',
            color: 'neutral',
          }}
        />
        <ActionMenu
          id='view_actions'
          btnText={t('draftManuals.menu.view')}
          items={setViewMenuItems()}
          anchorOrigin='left'
          btnProps={{
            size: 'small',
            variant: 'outlined',
            color: 'neutral',
          }}
        />
        {isFeatureEnabled('export-draft-manuals') && (
          <ActionMenu
            id='export_actions'
            btnText={t('draftManuals.menu.export')}
            items={setExportMenuItems()}
            btnProps={{
              size: 'small',
              variant: 'outlined',
              color: 'neutral',
            }}
          />
        )}
      </Stack>

      {isFeatureEnabled('export-draft-manuals') && (
        <ExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          exportType={exportType}
          node={node}
          showContentFilters={showContentFilters}
          currentPolicyData={currentPolicyData}
        />
      )}
    </>
  );
};

export default ManualActions;
